import {
  ResourceBlockerGroup,
  ResourceBlockerType,
  SecurityAssignmentStatus,
  Severity,
  type InventoryResource,
} from '@repo/api-gw-sdk';

import { hasSecurityIssue } from '@/components/security/securityUtils';

export type AlertLevel = 'high' | 'medium' | 'low';

export const ALERT_ICONS = {
  threats: 'material-symbols-gpp-maybe-outline',
  controls: 'material-symbols-assignment-late-outline',
  actions: 'material-symbols-settings-alert-outline',
} as const;

// A world's own icon without the alert mark, shown for its healthy and inactive states.
export const WORLD_ICONS = {
  threats: 'material-symbols-shield-outline',
  controls: 'material-symbols-assignment-outline',
  actions: 'material-symbols-settings-outline',
} as const;

const countOf = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

// The tooltip on an alert and the sheet it opens share one title, so both name the same finding.
export const THREAT_FINDINGS_TITLE = 'Threat findings';

export const violatedControlsTitle = (count: number) =>
  countOf(count, 'violated control', 'violated controls');

export const actionItemsTitle = (count: number) =>
  countOf(count, 'action item', 'action items');

const LEVEL_RANK: Record<AlertLevel, number> = { high: 3, medium: 2, low: 1 };

export const byLevelDescending = (a: AlertLevel, b: AlertLevel) =>
  LEVEL_RANK[b] - LEVEL_RANK[a];

const highestLevel = (levels: AlertLevel[]): AlertLevel | undefined =>
  [...levels].sort(byLevelDescending)[0];

export const hasThreatFindings = (resource: InventoryResource) =>
  resource.threatDetection === SecurityAssignmentStatus.SECURITY_ASSIGNED &&
  hasSecurityIssue(resource);

export const controlSeverityLevels: Record<Severity, AlertLevel> = {
  [Severity.High]: 'high',
  [Severity.Medium]: 'medium',
  [Severity.Low]: 'low',
};

export const openControlViolationsOf = (resource: InventoryResource) =>
  (resource.controlViolationCounts?.high ?? 0) +
  (resource.controlViolationCounts?.medium ?? 0) +
  (resource.controlViolationCounts?.low ?? 0);

// Muted violations are left out of the counts' levels, so a resource whose violations are all muted has no alert.
export const controlViolationLevelOf = (resource: InventoryResource) =>
  highestLevel(
    (['high', 'medium', 'low'] as const).filter(
      (level) => (resource.controlViolationCounts?.[level] ?? 0) > 0
    )
  );

export interface ActionItem {
  level: AlertLevel;
  title: string;
}

// The API carries no severity for an action item, so the few that only improve a working backup read as low.
const ACTION_ITEM_LEVELS: Partial<Record<ResourceBlockerType, AlertLevel>> = {
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_KMS_KEY_INACCESSIBLE]: 'low',
  [ResourceBlockerType.ACTION_REQUIRED_KEYSPACES_CDC_NOT_ENABLED]: 'low',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_SQL_VM_BACKUP_APPROACHING_CAPACITY]:
    'low',
};

export const actionItemLevelOf = (type: ResourceBlockerType): AlertLevel =>
  ACTION_ITEM_LEVELS[type] ?? 'high';

export const LAST_SNAPSHOT_PARTIAL_TITLE = 'Last snapshot was partial';

export const actionItemsOf = (resource: InventoryResource): ActionItem[] => {
  const required: ActionItem[] = (resource.statusProperties?.blockers ?? [])
    .filter(
      (blocker) =>
        blocker.group ===
        ResourceBlockerGroup.RESOURCE_BLOCKER_GROUP_ACTION_REQUIRED
    )
    .map((blocker) => ({
      level: blocker.statusMessageKey
        ? actionItemLevelOf(blocker.statusMessageKey)
        : 'high',
      title: blocker.statusMessage || 'Resolve the backup blocker',
    }));

  const issues: ActionItem[] = resource.issuesCount
    ? [{ level: 'medium', title: LAST_SNAPSHOT_PARTIAL_TITLE }]
    : [];

  return [...required, ...issues].sort((a, b) =>
    byLevelDescending(a.level, b.level)
  );
};

export const actionItemsLevelOf = (items: ActionItem[]) =>
  highestLevel(items.map((item) => item.level));

// Ranks a resource by the alerts its Alerts column shows: the most severe first, then how many alert tags, then
// how many violations and action items sit behind them.
export const alertRankOf = (resource: InventoryResource): number[] => {
  const items = actionItemsOf(resource);
  const levels = [
    controlViolationLevelOf(resource),
    actionItemsLevelOf(items),
  ].filter((level): level is AlertLevel => !!level);
  const top = highestLevel(levels);
  return [
    top ? LEVEL_RANK[top] : 0,
    levels.length,
    openControlViolationsOf(resource) + items.length,
  ];
};
